import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { WaveDocEditor, WhaleHandlerIdentity, WhaleMissionStep } from "@vida/wave-core";
import { formatWhaleChatReply, formatWhaleStepLines } from "@vida/wave-core";
import {
  appendActiveTurn,
  archiveActiveSession,
  beginNewWhaleChat,
  loadWhaleSessions,
  runWhaleTask,
  startActiveSessionIfNeeded,
  stashActiveSessionBeforeExecution,
  WHALE_DEFAULT_ICON,
  whaleSessionPreview,
  whaleSessionStatusLabel,
  whaleTargetLabel,
  type WhaleChatSession,
  type WhaleChatTurn,
  type WaveWhaleTarget,
} from "../../services/waveWhale";
import {
  fetchWaveClientConfig,
  getWhaleConfigMissing,
  isWhaleConfigured,
  whaleConfigMissingLabel,
} from "../../services/waveClientConfig";
import { createWaveApiConfig } from "../../services/waveSession";
import { WaveAgentAvatar } from "../presence/WaveAgentAvatar";
import { WhaleChatMessageBody } from "./WhaleChatMessageBody";
import { WhaleTypingIndicator } from "./WhaleTypingIndicator";

export type { WaveWhaleTarget };

type Props = {
  waveId: string;
  spaceId: string;
  target: WaveWhaleTarget;
  editor: WaveDocEditor | null;
  launchedBy: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

type PanelView = "chat" | "history-list" | "history-detail";

const DEFAULT_WHALE: WhaleHandlerIdentity = {
  agentId: "whale",
  agentName: "Whale",
  agentIcon: WHALE_DEFAULT_ICON,
  agentIconS3Key: null,
};

function formatSessionDate(iso?: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
}

function TurnAttachments({ attachments }: { attachments?: WhaleChatTurn["attachments"] }) {
  if (!attachments?.length) return null;
  return attachments.map((att) =>
    att.mime?.startsWith("image/") ? (
      <img
        key={att.id}
        src={att.url}
        alt="Allegato generato"
        className="wave-attachment-image"
        style={{ maxWidth: "100%", marginTop: "0.5rem", borderRadius: 8 }}
      />
    ) : null,
  );
}

function TurnBlock({ turn }: { turn: WhaleChatTurn }) {
  return (
    <div className="wave-whale-turn">
      <div className="wave-whale-message wave-whale-message--user">
        <span className="wave-whale-message-label">Tu</span>
        <p>{turn.question}</p>
      </div>
      <div className="wave-whale-message wave-whale-message--assistant">
        <span className="wave-whale-message-label wave-whale-message-label--agent">
          <WaveAgentAvatar
            name={turn.agentName}
            emoji={turn.agentIcon}
            iconS3Key={turn.agentIconS3Key}
            size={18}
          />
          {turn.agentName}
        </span>
        <WhaleChatMessageBody text={turn.answer} raw />
        <TurnAttachments attachments={turn.attachments} />
      </div>
    </div>
  );
}

export function WaveWhalePanel({
  waveId,
  spaceId,
  target,
  editor,
  launchedBy,
  open,
  onOpenChange,
  onSaved,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turns, setTurns] = useState<WhaleChatTurn[]>([]);
  const [archive, setArchive] = useState<WhaleChatSession[]>([]);
  const [priorTurnsExpanded, setPriorTurnsExpanded] = useState(false);
  const [panelView, setPanelView] = useState<PanelView>("chat");
  const [selectedSession, setSelectedSession] = useState<WhaleChatSession | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [streamingReply, setStreamingReply] = useState("");
  const [liveMissionSteps, setLiveMissionSteps] = useState<WhaleMissionStep[]>([]);
  const [liveAttachments, setLiveAttachments] = useState<
    Array<{ id: string; url: string; mime?: string }>
  >([]);
  const [configRevision, setConfigRevision] = useState(0);
  const [configLoading, setConfigLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(true);
  const [activeAgent, setActiveAgent] = useState<WhaleHandlerIdentity>(DEFAULT_WHALE);
  const [taskStartedAt, setTaskStartedAt] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  const targetLabel = whaleTargetLabel(target);
  const whaleReady = isWhaleConfigured();
  const missingConfig = getWhaleConfigMissing();
  void configRevision;
  void taskStartedAt;

  const reloadSessions = useCallback(() => {
    const sessions = loadWhaleSessions(waveId, target.blockId);
    setTurns(sessions.active?.turns ?? []);
    setArchive(sessions.archive);
    return sessions;
  }, [waveId, target.blockId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setConfigLoading(true);
    void (async () => {
      try {
        await fetchWaveClientConfig(createWaveApiConfig());
      } finally {
        if (!cancelled) {
          setConfigLoading(false);
          setConfigRevision((n) => n + 1);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    reloadSessions();
    setDraft("");
    setError(null);
    setPendingQuestion(null);
    setStreamingReply("");
    setLiveMissionSteps([]);
    setLiveAttachments([]);
    setComposerOpen(true);
    setActiveAgent(DEFAULT_WHALE);
    setTaskStartedAt(null);
    setPriorTurnsExpanded(false);
    setPanelView("chat");
    setSelectedSession(null);
  }, [waveId, target.blockId, reloadSessions]);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    const sessions = loadWhaleSessions(waveId, target.blockId);
    if (sessions.active?.turns.length) {
      archiveActiveSession(waveId, target.blockId, { status: "abandoned" });
    }
    const fresh = reloadSessions();
    setComposerOpen(true);
    setPendingQuestion(null);
    setStreamingReply("");
    setLiveMissionSteps([]);
    setLiveAttachments([]);
    setPanelView("chat");
    setSelectedSession(null);
    setPriorTurnsExpanded(false);
    setDraft("");
    setError(null);
    if (!fresh.active?.turns.length) {
      setTurns([]);
      setTaskStartedAt(null);
      setActiveAgent(DEFAULT_WHALE);
    }
  }, [open, reloadSessions, target.blockId, waveId]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [turns, busy, pendingQuestion, streamingReply, error, liveMissionSteps, liveAttachments, panelView]);

  const handleClose = useCallback(() => {
    const sessions = loadWhaleSessions(waveId, target.blockId);
    if (sessions.active && sessions.active.turns.length > 0) {
      archiveActiveSession(waveId, target.blockId, { status: "abandoned" });
      reloadSessions();
    }
    setPanelView("chat");
    setSelectedSession(null);
    flushSync(() => {
      onOpenChange(false);
    });
  }, [onOpenChange, reloadSessions, target.blockId, waveId]);

  const forceCloseWhalePanel = useCallback(() => {
    flushSync(() => {
      onOpenChange(false);
    });
  }, [onOpenChange]);

  const resetWhaleChatUi = useCallback(() => {
    setTurns([]);
    setDraft("");
    setError(null);
    setPendingQuestion(null);
    setStreamingReply("");
    setLiveMissionSteps([]);
    setLiveAttachments([]);
    setComposerOpen(true);
    setPriorTurnsExpanded(false);
    setPanelView("chat");
    setSelectedSession(null);
    setTaskStartedAt(null);
    setActiveAgent(DEFAULT_WHALE);
  }, []);

  const handleNewChat = useCallback(() => {
    if (busy) return;
    beginNewWhaleChat(waveId, target.blockId);
    reloadSessions();
    resetWhaleChatUi();
    forceCloseWhalePanel();
  }, [busy, forceCloseWhalePanel, reloadSessions, resetWhaleChatUi, target.blockId, waveId]);

  const handleExecutionStarted = useCallback(
    (next: WhaleHandlerIdentity, jobId?: string) => {
      stashActiveSessionBeforeExecution(waveId, target.blockId, {
        jobId,
        handler: next,
      });
      setTurns([]);
      setPriorTurnsExpanded(false);
      reloadSessions();
      setComposerOpen(false);
      setPendingQuestion(null);
      setStreamingReply("");
      setLiveMissionSteps([]);
      setLiveAttachments([]);
      setActiveAgent(next);
      forceCloseWhalePanel();
    },
    [forceCloseWhalePanel, reloadSessions, target.blockId, waveId],
  );

  const submitQuestion = useCallback(async () => {
    const question = draft.trim();
    if (!question || busy || !editor) return;
    if (!isWhaleConfigured()) {
      const missing = getWhaleConfigMissing().map(whaleConfigMissingLabel).join(", ");
      setError(
        missing
          ? `Config Whale incompleta: ${missing}. Imposta wave_whale_agent_id in Terraform (agente ORCHESTRATOR con app wave in Currents) e rigenera .env con bash cloud/scripts/wave-web-sync-env.sh.`
          : "Whale non configurato per questo ambiente.",
      );
      return;
    }
    startActiveSessionIfNeeded(waveId, target.blockId);
    const priorTurns = loadWhaleSessions(waveId, target.blockId).active?.turns ?? [];

    setDraft("");
    setError(null);
    setPendingQuestion(question);
    setStreamingReply("");
    setLiveMissionSteps([]);
    setLiveAttachments([]);
    setBusy(true);
    setActiveAgent(DEFAULT_WHALE);
    setComposerOpen(true);
    setPriorTurnsExpanded(false);

    try {
      let answer = "";
      let handler = DEFAULT_WHALE;
      let executed = false;
      let executionJobId = "";
      const turnAttachments: Array<{ id: string; url: string; mime?: string }> = [];

      await runWhaleTask({
        waveId,
        spaceId,
        target,
        goal: question,
        launchedBy,
        editor,
        activeTurns: priorTurns,
        callbacks: {
          onMeta: ({ jobId, agentName, agentIcon, agentIconS3Key }) => {
            executionJobId = jobId;
            setTaskStartedAt(new Date().toISOString());
            setActiveAgent({
              agentId: DEFAULT_WHALE.agentId,
              agentName: agentName.trim() || DEFAULT_WHALE.agentName,
              agentIcon: agentIcon.trim() || DEFAULT_WHALE.agentIcon,
              agentIconS3Key: agentIconS3Key?.trim() || null,
            });
          },
          onHandlerChange: (next) => {
            handler = next;
            setActiveAgent(next);
          },
          onExecutionStarted: (next) => {
            handler = next;
            executed = true;
            handleExecutionStarted(next, executionJobId || undefined);
          },
          onStep: (step) => {
            setLiveMissionSteps((prev) => [...prev, step]);
          },
          onDelta: (chunk) => {
            setStreamingReply((prev) => prev + chunk);
          },
          onAttachment: ({ id, presignedUrl, mime }) => {
            if (!presignedUrl) return;
            const att = { id, url: presignedUrl, mime };
            turnAttachments.push(att);
            setLiveAttachments((prev) => [...prev, att]);
          },
          onDone: ({ outputText, handler: doneHandler, executed: didExecute }) => {
            answer = formatWhaleChatReply(outputText);
            handler = doneHandler;
            executed = executed || didExecute;
            setActiveAgent(doneHandler);
          },
        },
      });

      if (executed) {
        const turn: WhaleChatTurn = {
          question,
          answer:
            answer.trim() ||
            "Missione avviata — segui gli aggiornamenti nel thread del widget.",
          agentName: handler.agentName,
          agentIcon: handler.agentIcon,
          agentIconS3Key: handler.agentIconS3Key ?? null,
          attachments: turnAttachments.length > 0 ? turnAttachments : undefined,
        };
        appendActiveTurn(waveId, target.blockId, turn);
        archiveActiveSession(waveId, target.blockId, {
          status: "executed",
          jobId: executionJobId,
          handler,
        });
        reloadSessions();
        setPendingQuestion(null);
        setStreamingReply("");
        setLiveMissionSteps([]);
        setLiveAttachments([]);
        setTaskStartedAt(null);
        setActiveAgent(DEFAULT_WHALE);
        onSaved?.();
      } else {
        const turn: WhaleChatTurn = {
          question,
          answer: formatWhaleChatReply(answer).trim() || "(Nessuna risposta testuale.)",
          agentName: handler.agentName,
          agentIcon: handler.agentIcon,
          agentIconS3Key: handler.agentIconS3Key ?? null,
          attachments: turnAttachments.length > 0 ? turnAttachments : undefined,
        };
        appendActiveTurn(waveId, target.blockId, turn);
        reloadSessions();
        setPendingQuestion(null);
        setStreamingReply("");
        setLiveMissionSteps([]);
        setLiveAttachments([]);
        setComposerOpen(true);
        onSaved?.();
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Errore Whale.";
      setError(message);
      setDraft(question);
      setComposerOpen(true);
    } finally {
      setBusy(false);
    }
  }, [busy, draft, editor, handleExecutionStarted, launchedBy, onSaved, reloadSessions, target, waveId]);

  const agentLabel = activeAgent.agentName || DEFAULT_WHALE.agentName;
  const priorTurns = turns.length > 1 ? turns.slice(0, -1) : [];
  const latestTurn = turns.length > 0 ? turns[turns.length - 1] : null;
  const streamingReplyDisplay = formatWhaleChatReply(streamingReply).trim();

  if (!open) return null;

  const headerTitle =
    panelView === "history-list"
      ? "Precedenti richieste"
      : panelView === "history-detail"
        ? "Dettaglio sessione"
        : targetLabel;

  return (
    <div
      className={`wave-whale-popover ${expanded ? "is-expanded" : ""}`}
      role="dialog"
      aria-label={`Whale — ${targetLabel}`}
    >
      <header className="wave-whale-popover-header">
        <WaveAgentAvatar
          name={DEFAULT_WHALE.agentName}
          emoji={DEFAULT_WHALE.agentIcon}
          size={24}
          className="wave-whale-popover-agent-icon"
        />
        <h3 className="wave-whale-popover-heading" title={headerTitle}>
          {headerTitle}
        </h3>
        <div className="wave-whale-popover-spacer" aria-hidden />
        {panelView === "chat" && (
          <button
            type="button"
            className="wave-whale-header-btn"
            disabled={busy}
            onClick={handleNewChat}
            title="Archivia la chat corrente e inizia da zero"
          >
            Nuova chat
          </button>
        )}
        {panelView === "chat" && archive.length > 0 && (
          <button
            type="button"
            className="wave-whale-header-btn"
            onClick={() => setPanelView("history-list")}
          >
            Storico ({archive.length})
          </button>
        )}
        {panelView !== "chat" && (
          <button
            type="button"
            className="wave-whale-header-btn"
            onClick={() => {
              setPanelView(panelView === "history-detail" ? "history-list" : "chat");
              setSelectedSession(null);
            }}
            aria-label="Indietro"
          >
            ←
          </button>
        )}
        <button
          type="button"
          className="wave-whale-header-icon-btn"
          onClick={() => setExpanded((v) => !v)}
          aria-label="Espandi"
        >
          {expanded ? "↙" : "↗"}
        </button>
        <button
          type="button"
          className="wave-whale-header-icon-btn"
          onClick={handleClose}
          aria-label="Chiudi"
        >
          ✕
        </button>
      </header>

      <div className="wave-whale-message-list" ref={listRef}>
        {panelView === "history-list" && (
          <ul className="wave-whale-session-list">
            {archive.map((session) => (
              <li key={session.id}>
                <button
                  type="button"
                  className="wave-whale-session-item"
                  onClick={() => {
                    setSelectedSession(session);
                    setPanelView("history-detail");
                  }}
                >
                  <span className="wave-whale-session-item-preview">{whaleSessionPreview(session)}</span>
                  <span className="wave-whale-session-item-meta">
                    {formatSessionDate(session.endedAt ?? session.startedAt)} ·{" "}
                    {whaleSessionStatusLabel(session.status)}
                    {session.handler?.agentName ? ` · ${session.handler.agentName}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {panelView === "history-detail" && selectedSession && (
          <div className="wave-whale-session-detail">
            <div className="wave-whale-session-detail-meta">
              <p>
                <strong>Stato:</strong> {whaleSessionStatusLabel(selectedSession.status)}
              </p>
              <p>
                <strong>Periodo:</strong> {formatSessionDate(selectedSession.startedAt)}
                {selectedSession.endedAt ? ` → ${formatSessionDate(selectedSession.endedAt)}` : ""}
              </p>
              {selectedSession.jobId && (
                <p>
                  <strong>Job:</strong> <code>{selectedSession.jobId}</code>
                </p>
              )}
              {selectedSession.handler && (
                <p className="wave-whale-session-detail-handler">
                  <WaveAgentAvatar
                    name={selectedSession.handler.agentName}
                    emoji={selectedSession.handler.agentIcon}
                    iconS3Key={selectedSession.handler.agentIconS3Key}
                    size={20}
                  />
                  <span>{selectedSession.handler.agentName}</span>
                </p>
              )}
            </div>
            {selectedSession.turns.map((turn, index) => (
              <TurnBlock key={`${selectedSession.id}-${index}`} turn={turn} />
            ))}
          </div>
        )}

        {panelView === "chat" && (
          <>
            {!whaleReady && !configLoading && (
              <div className="wave-whale-config-hint">
                <p className="wave-whale-error">
                  Whale non è configurato per questo ambiente.
                </p>
                {missingConfig.length > 0 && (
                  <ul className="wave-whale-config-missing">
                    {missingConfig.map((field) => (
                      <li key={field}>{whaleConfigMissingLabel(field)}</li>
                    ))}
                  </ul>
                )}
                <p className="wave-whale-config-help">
                  In Currents crea/abilita l&apos;agente orchestrator Whale (app <strong>wave</strong>),
                  poi imposta <code>wave_whale_agent_id</code> in Terraform e applica. In locale:
                  <code> bash cloud/scripts/wave-web-sync-env.sh</code> e riavvia <code>npm run wave:dev</code>.
                </p>
              </div>
            )}
            {configLoading && <p className="wave-whale-busy">Caricamento config Whale…</p>}
            {turns.length === 0 && !busy && !pendingQuestion && whaleReady && composerOpen && (
              <p className="wave-whale-empty">
                Chiedi a Whale cosa fare su questo widget — la risposta compare nel thread.
              </p>
            )}
            {priorTurns.length > 0 && (
              <div className="wave-whale-prior-turns">
                <button
                  type="button"
                  className="wave-whale-prior-turns-toggle"
                  aria-expanded={priorTurnsExpanded}
                  onClick={() => setPriorTurnsExpanded((v) => !v)}
                >
                  {priorTurnsExpanded
                    ? "Nascondi messaggi precedenti"
                    : `${priorTurns.length} messaggio${priorTurns.length === 1 ? "" : "i"} precedente${priorTurns.length === 1 ? "" : "i"}`}
                </button>
                {priorTurnsExpanded &&
                  priorTurns.map((turn, index) => (
                    <TurnBlock key={`prior-${index}-${turn.question.slice(0, 16)}`} turn={turn} />
                  ))}
              </div>
            )}
            {latestTurn && <TurnBlock turn={latestTurn} />}
            {pendingQuestion && (
              <div className="wave-whale-turn">
                <div className="wave-whale-message wave-whale-message--user">
                  <span className="wave-whale-message-label">Tu</span>
                  <p>{pendingQuestion}</p>
                </div>
                {busy && composerOpen && (
                  <div className="wave-whale-message wave-whale-message--assistant">
                    <span className="wave-whale-message-label wave-whale-message-label--agent">
                      <WaveAgentAvatar
                        name={agentLabel}
                        emoji={activeAgent.agentIcon}
                        iconS3Key={activeAgent.agentIconS3Key}
                        size={18}
                      />
                      {agentLabel}
                    </span>
                    {streamingReplyDisplay ? (
                      <WhaleChatMessageBody text={streamingReply} />
                    ) : (
                      <WhaleTypingIndicator />
                    )}
                    <TurnAttachments attachments={liveAttachments} />
                  </div>
                )}
                {busy && !composerOpen && (
                  <div className="wave-whale-message wave-whale-message--assistant">
                    <span className="wave-whale-message-label wave-whale-message-label--agent">
                      <WaveAgentAvatar
                        name={agentLabel}
                        emoji={activeAgent.agentIcon}
                        iconS3Key={activeAgent.agentIconS3Key}
                        size={18}
                      />
                      {agentLabel}
                    </span>
                    {liveMissionSteps.length > 0 ? (
                      <ul className="wave-whale-steps wave-whale-steps--inline">
                        {formatWhaleStepLines(liveMissionSteps)
                          .split("\n")
                          .map((line, i) => (
                            <li key={`${i}-${line}`}>{line.replace(/^•\s*/, "")}</li>
                          ))}
                      </ul>
                    ) : (
                      <WhaleTypingIndicator />
                    )}
                    <TurnAttachments attachments={liveAttachments} />
                  </div>
                )}
              </div>
            )}
            {error && <p className="wave-whale-error">{error}</p>}
          </>
        )}
      </div>

      {panelView === "chat" && composerOpen && (
        <footer className="wave-whale-composer">
          <textarea
            rows={2}
            value={draft}
            placeholder="Cosa deve fare Whale su questo widget?"
            disabled={busy || !editor}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void submitQuestion();
              }
            }}
          />
          <button
            type="button"
            disabled={busy || !draft.trim() || !editor || !whaleReady}
            onClick={() => void submitQuestion()}
          >
            Invia
          </button>
        </footer>
      )}
    </div>
  );
}
